import { GameServices } from "game/GameServices";
import { Signal } from "type-signals";
import { DragonsVsPandasGameModel } from "game/DragonsVsPandasGameModel";
import { ReelStopEase, SpinConfig, SpinType } from "components/reels/ReelsModel";
import { copyToClipboard } from "utils/Misc";
import { VSScreen } from "components/vsscreen/VSScreen";
import { VSRespinPanel } from "components/VSRespinPanel";
import { VSResultPanel } from "components/VSResultPanel";
import { VSOption } from "servertypes/SharedTypes";
import { GameDevGUI } from "@westpierstudio/core-module-oryx/utils/devgui";

class DragonsVsPandasDebugGUISignals  {
    printFlow = new Signal();
    setFlowVerbose = new Signal<(value: boolean) => void>();
}

export class DragonsVsPandasDebugGUI extends GameDevGUI{
    customSignals = new DragonsVsPandasDebugGUISignals();

    constructor(protected override readonly services: GameServices, private readonly model: DragonsVsPandasGameModel,) {
        super(services);

        this.setupFlowLogging();
        this.setupTickerDebug();
    }

    setupVSScreenDebug(vsScreen: VSScreen): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableVSScreenDebug) {

            const vsTab = this.gui.addSubSection(`VS Screen`);
            vsTab.open();

            this.gui.addButton(`Show`, () => {
                vsScreen.show();
            }, vsTab);
        }
    }

    setupVSRespinPanelDebug(panel: VSRespinPanel): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableVSRespinPanelDebug) {

            const respinTab = this.gui.addSubSection(`VS Respin Panel`);
            respinTab.open();

            this.gui.addButton(`Show`, () => {
                panel.show(VSOption.PANDA);
            }, respinTab);
        }
    }

    setupVSResultPanelsDebug(dragonsPanel: VSResultPanel, pandasPanel: VSResultPanel): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableVSResultPanelsDebug) {

            const resultsTab = this.gui.addSubSection(`VS Result Panels`);
            resultsTab.open();

            this.gui.addButton(`Show Dragon Win`, () => {
                dragonsPanel.showWin(100, true);
                pandasPanel.showLoss(0, false);
            }, resultsTab);

            this.gui.addButton(`Show Panda Win`, () => {
                dragonsPanel.showLoss(0, false);
                pandasPanel.showWin(100, true);
            }, resultsTab);

            this.gui.addButton(`Show Draw`, () => {
                dragonsPanel.showDraw(50);
                pandasPanel.showDraw(50);
            }, resultsTab);
        }
    }

    private setupFlowLogging() {

        const flowLoggingTab = this.gui.addSubSection(`Flow Logging`);

        const verbose = {value: false};
        this.gui.addButton(`Print`, () => this.customSignals.printFlow.dispatch(), flowLoggingTab);
        this.gui.addCheckBox(`Verbose`, verbose, () => this.customSignals.setFlowVerbose.dispatch(verbose.value), flowLoggingTab);
    }

    private setupTickerDebug() {

        const ticker = this.services.application.ticker;
        const tickerDebugTab = this.gui.addSubSection(`Ticker Debug`);
        const tickerFPSGUI = this.gui.addString(`FPS`, ticker.FPS.toString(), tickerDebugTab);
        const tickerMaxFPSGUI = this.gui.addString(`Max FPS`, ticker.maxFPS.toString(), tickerDebugTab);
        const tickerMinFPSGUI = this.gui.addString(`Min FPS`, ticker.minFPS.toString(), tickerDebugTab);
        const tickerSpeedGUI = this.gui.addString(`Speed`, ticker.speed.toString(), tickerDebugTab);
        const tickerListenersGUI = this.gui.addString(`Listeners`, ticker.count.toString(), tickerDebugTab);

        const updateTickerDebug = function () {
            tickerFPSGUI.setValue(Math.round(ticker.FPS).toString());
            tickerMaxFPSGUI.setValue(ticker.maxFPS ? ticker.maxFPS.toString() : `Not Limited`);
            tickerMinFPSGUI.setValue(ticker.minFPS.toString());
            tickerSpeedGUI.setValue(ticker.speed.toString());
            tickerListenersGUI.setValue(ticker.count.toString());
        };

        ticker.add(updateTickerDebug);
    }

    private setupSpinConfigEditor(): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableSpinConfigEditor) {

            const spinSection = this.gui.addSubSection(`SpinConfig`);
            const spinTypes: SpinType[] = Object.keys(this.services.resources.getSetting(`reelSpinSettings`)) as SpinType[];

            for (const type of spinTypes) {

                const spinTypeSection = spinSection.addFolder(type);

                const spinConfig = this.model.reels.spinConfigs.get(type) as SpinConfig;
                const spinSettings = this.model.reels.getSettingsForConfig(spinConfig);

                for (const [key, item] of Object.entries(spinSettings)) {

                    const obj = {value: item};

                    if (typeof item === `number`) {

                        this.gui.addNumber(key, obj, () => {

                            (spinConfig as any)[key] = obj.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);

                    } else if (typeof item === `boolean`) {

                        this.gui.addCheckBox(key, obj, () => {

                            (spinConfig as any)[key] = obj.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);
                    } else {

                        const list = Object.values(ReelStopEase);
                        const state = {value: list[list.indexOf(item)]};

                        this.gui.addDropdown(key, list, state, () => {

                            (spinConfig as any)[key] = state.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);
                    }
                }

                this.gui.addButton(`Copy`, () => {

                    const spinSettings = this.model.reels.getSettingsForConfig(spinConfig);
                    copyToClipboard(JSON.stringify(spinSettings, null, `\t`));
                }, spinTypeSection);
            }

            this.gui.addButton(`Reset Defaults`, () => {
                this.model.reels.restoreDefaultSpinConfigs();
                this.gui.restoreSectionDefaults(spinSection);
            }, spinSection);
        }
    }
}